import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { bookingsService, addonsService, itemsService, bookingAddonsService } from '../services/api';
import { getAccommodationDisplay, getCourseDisplay } from '../utils/bookingHelpers';
import { Ban as BanIcon, Hourglass as HourglassIcon, Search as SearchIcon } from 'lucide-react';

const AddOnServicesPage = ({ user }) => {
  // State management
  const [step, setStep] = useState(1); // 1: Select Booking, 2: Select Service, 3: Select Item (if applicable), 4: Confirm
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [addOnServices, setAddOnServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceItems, setServiceItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Check if user has required access
  const hasAccess = ['STAFF', 'PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role);

  // Load bookings on mount
  useEffect(() => {
    if (hasAccess) {
      loadBookings();
      loadAddOnServices();
    }
  }, [hasAccess]);

  // Filter bookings based on search and status
  useEffect(() => {
    filterBookings();
  }, [searchTerm, statusFilter, bookings]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingsService.getAll({ limit: 1000 });
      const bookingsList = response.data?.bookings || response.data || [];
      setBookings(bookingsList);
      setFilteredBookings(bookingsList);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadAddOnServices = async () => {
    try {
      const response = await addonsService.getAll();
      const services = response.data?.services || response.data || [];
      setAddOnServices(services.filter(s => s.isActive));
    } catch (error) {
      console.error('Error loading addon services:', error);
      toast.error('Failed to load addon services');
    }
  };

  const loadItemsForService = async (serviceId) => {
    try {
      setLoading(true);
      const response = await itemsService.getByService(serviceId, { isActive: true });
      const items = response.data?.items || [];
      setServiceItems(items);
      return items;
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load items for service');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        const bookingNumber = booking.bookingNumber?.toLowerCase() || '';
        const guestName = booking.guest?.name?.toLowerCase() || '';
        const guestEmail = booking.guest?.email?.toLowerCase() || '';
        return bookingNumber.includes(term) || guestName.includes(term) || guestEmail.includes(term);
      });
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    setFilteredBookings(filtered);
  };

  const handleBookingSelect = (booking) => {
    setSelectedBooking(booking);
    setStep(2);
  };

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    
    // Check if service has items
    const items = await loadItemsForService(service.id);
    
    if (items.length > 0) {
      // Go to item selection
      setStep(3);
    } else {
      // Skip to confirmation (no items for this service)
      setSelectedItem(null);
      setStep(4);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setStep(4);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const addonData = {
        bookingId: selectedBooking.id,
        addOnServiceId: selectedService.id,
        quantity: parseInt(quantity),
        price: selectedItem ? selectedItem.unitPrice : selectedService.price,
      };

      // Add itemId if item was selected
      if (selectedItem) {
        addonData.itemId = selectedItem.id;
      }

      await bookingAddonsService.create(addonData);

      toast.success(`Add-on service "${selectedService.name}" added to booking ${selectedBooking.bookingNumber}`);

      // Reset to step 1
      resetFlow();
    } catch (error) {
      console.error('Error creating booking addon:', error);
      const errData = error.response?.data || {};
      if (errData.stockDepleted) {
        toast.error(
          `Out of Stock: "${errData.itemName}" has 0 units remaining. Please restock before adding this service.`,
          { autoClose: 7000 }
        );
      } else if (errData.insufficientStock) {
        toast.error(
          `Insufficient Stock: Only ${errData.availableQuantity} unit(s) of "${errData.itemName}" available. You requested ${errData.requestedQuantity}.`,
          { autoClose: 7000 }
        );
      } else if (errData.requiresItem) {
        toast.error(errData.error || 'Please select an item for this service.', { autoClose: 5000 });
        setStep(3);
      } else {
        toast.error(error.error || errData.error || 'Failed to add service to booking');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep(1);
    setSelectedBooking(null);
    setSelectedService(null);
    setSelectedItem(null);
    setServiceItems([]);
    setQuantity(1);
    loadBookings(); // Refresh bookings
  };

  const goBack = () => {
    if (step === 4 && serviceItems.length > 0) {
      setStep(3);
      setSelectedItem(null);
    } else if (step === 4 && serviceItems.length === 0) {
      setStep(2);
      setSelectedService(null);
    } else if (step === 3) {
      setStep(2);
      setSelectedService(null);
      setServiceItems([]);
    } else if (step === 2) {
      setStep(1);
      setSelectedBooking(null);
    }
  };

  if (!hasAccess) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <p>Required roles: STAFF, PROPERTYADMIN, or SUPERADMIN</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#F5F3EB', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#36433B' }}>
          Add-On Services Management
        </h1>
        <p style={{ margin: '8px 0 0 0', color: '#5F6B61' }}>
          Add services and items to guest bookings
        </p>

        {/* Breadcrumb */}
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <span style={{ color: step === 1 ? '#1A3E2A' : '#A5A89B', fontWeight: step === 1 ? '600' : '400' }}>
            1. Select Booking
          </span>
          <span style={{ color: '#A5A89B' }}>→</span>
          <span style={{ color: step === 2 ? '#1A3E2A' : '#A5A89B', fontWeight: step === 2 ? '600' : '400' }}>
            2. Select Service
          </span>
          <span style={{ color: '#A5A89B' }}>→</span>
          <span style={{ color: step === 3 ? '#1A3E2A' : '#A5A89B', fontWeight: step === 3 ? '600' : '400' }}>
            3. Select Item
          </span>
          <span style={{ color: '#A5A89B' }}>→</span>
          <span style={{ color: step === 4 ? '#1A3E2A' : '#A5A89B', fontWeight: step === 4 ? '600' : '400' }}>
            4. Confirm
          </span>
        </div>
      </div>

      {/* Step 1: Select Booking */}
      {step === 1 && (
        <div>
          {/* Filters */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Booking number, guest name, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="CHECKEDIN">Checked In</option>
                  <option value="CHECKEDOUT">Checked Out</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '12px', fontSize: '14px', color: '#5F6B61' }}>
              Showing {filteredBookings.length} of {bookings.length} bookings
            </div>
          </div>

          {/* Bookings Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
              <p>Loading bookings...</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '16px'
            }}>
              {filteredBookings.map(booking => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onSelect={handleBookingSelect}
                />
              ))}
            </div>
          )}

          {filteredBookings.length === 0 && !loading && (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}><SearchIcon size="1em" aria-hidden="true" /></div>
              <h3 style={{ margin: '0 0 8px 0' }}>No Bookings Found</h3>
              <p style={{ margin: 0, color: '#5F6B61' }}>
                {searchTerm || statusFilter ? 'Try adjusting your filters' : 'No bookings available'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Service */}
      {step === 2 && selectedBooking && (
        <div>
          {/* Selected Booking Info */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                  Selected Booking: {selectedBooking.bookingNumber}
                </h3>
                <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                  Guest: {selectedBooking.guest?.name} | {getAccommodationDisplay(selectedBooking)}
                </p>
              </div>
              <button
                onClick={goBack}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#5F6B61',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ← Change Booking
              </button>
            </div>
          </div>

          {/* Services Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {addOnServices.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onSelect={handleServiceSelect}
              />
            ))}
          </div>

          {addOnServices.length === 0 && (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p>No addon services available</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Select Item */}
      {step === 3 && selectedService && (
        <div>
          {/* Service Info */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                  Selected Service: {selectedService.name}
                </h3>
                <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                  Select a specific item/variant for this service
                </p>
              </div>
              <button
                onClick={goBack}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#5F6B61',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ← Change Service
              </button>
            </div>
          </div>

          {/* Out-of-stock warning */}
          {!loading && serviceItems.length > 0 && serviceItems.every(i => i.stockStatus === 'OUT_OF_STOCK') && (
            <div style={{
              backgroundColor: '#F9EEEB',
              border: '1px solid #D48A7C',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '24px' }}><BanIcon size="1em" aria-hidden="true" /></span>
              <div>
                <div style={{ fontWeight: '600', color: '#5F241B', fontSize: '15px' }}>
                  All items are out of stock
                </div>
                <div style={{ color: '#8E3628', fontSize: '13px', marginTop: '2px' }}>
                  No items available for "{selectedService.name}". Please restock before adding this service.
                </div>
              </div>
            </div>
          )}

          {/* Items Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
              <p>Loading items...</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px'
            }}>
              {serviceItems.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onSelect={handleItemSelect}
                />
              ))}
            </div>
          )}

          {serviceItems.length === 0 && !loading && (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p>No items available for this service</p>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <div style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          maxWidth: '600px',
          margin: '0 auto',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600' }}>
            Confirm Add-On Service
          </h2>

          {/* Summary */}
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F5F3EB', borderRadius: '8px' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Booking:</strong> {selectedBooking.bookingNumber}
              <br />
              <span style={{ fontSize: '14px', color: '#5F6B61' }}>
                Guest: {selectedBooking.guest?.name}
              </span>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong>Service:</strong> {selectedService.name}
              <br />
              <span style={{ fontSize: '14px', color: '#5F6B61' }}>
                {selectedService.description}
              </span>
            </div>

            {selectedItem && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Item:</strong> {selectedItem.name}
                <br />
                <span style={{ fontSize: '14px', color: '#5F6B61' }}>
                  SKU: {selectedItem.sku} | Stock: {selectedItem.quantity}
                </span>
                <br />
                <span style={{
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: selectedItem.stockStatus === 'IN_STOCK' ? '#E1E9E2' : '#F3E6CB',
                  color: selectedItem.stockStatus === 'IN_STOCK' ? '#163626' : '#B68235'
                }}>
                  {selectedItem.stockStatus}
                </span>
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quantity:
              </label>
              <input
                type="number"
                min="1"
                max={selectedItem?.quantity || 100}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{
                  width: '100px',
                  padding: '8px 12px',
                  border: '1px solid #E3DFD3',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E3DFD3' }}>
              <strong>Price:</strong> ₹{(selectedItem ? selectedItem.unitPrice : selectedService.price) * quantity} 
              <span style={{ fontSize: '14px', color: '#5F6B61', marginLeft: '8px' }}>
                (₹{selectedItem ? selectedItem.unitPrice : selectedService.price} × {quantity})
              </span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={goBack}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: 'white',
                color: '#36433B',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              ← Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 2,
                padding: '12px 24px',
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Adding...' : 'Add to Booking'}
            </button>
          </div>

          <button
            onClick={resetFlow}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '10px',
              backgroundColor: 'transparent',
              color: '#5F6B61',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
          >
            Cancel and Start Over
          </button>
        </div>
      )}
    </div>
  );
};

// Booking Card Component
const BookingCard = ({ booking, onSelect }) => {
  const statusColors = {
    CONFIRMED: { bg: '#E1E9E2', text: '#163626' },
    CHECKEDIN: { bg: '#E1E9E2', text: '#163626' },
    CHECKEDOUT: { bg: '#F1E0D4', text: '#975534' },
    PENDING: { bg: '#F3E6CB', text: '#B68235' },
    CANCELLED: { bg: '#F2D9D3', text: '#8E3628' },
  };

  const status = statusColors[booking.status] || { bg: '#EEEBE1', text: '#5F6B61' };
  
  // Check if booking is from BOOKING_ENGINE
  const isBookingEngine = booking.bookingPlatform === 'BOOKING_ENGINE';

  return (
    <div
      onClick={() => onSelect(booking)}
      style={{
        backgroundColor: isBookingEngine ? '#E1E9E2' : 'white', // Light blue shade for booking engine bookings
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: isBookingEngine ? '2px solid #9DB7A5' : '2px solid transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(26, 62, 42, 0.3)';
        e.currentTarget.style.borderColor = '#1A3E2A';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = isBookingEngine ? '#9DB7A5' : 'transparent';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#36433B' }}>
          {booking.bookingNumber}
        </h3>
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: status.bg,
          color: status.text
        }}>
          {booking.status}
        </span>
      </div>

      {/* Guest Info */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '16px', fontWeight: '500', color: '#1A3E2A', marginBottom: '4px' }}>
          {booking.guest?.name || 'N/A'}
        </div>
        <div style={{ fontSize: '14px', color: '#5F6B61' }}>
          {booking.guest?.email || 'N/A'}
        </div>
      </div>

      {/* Accommodation & Course */}
      <div style={{ marginBottom: '12px', fontSize: '14px', color: '#36433B' }}>
        <div style={{ marginBottom: '4px' }}>
          {getAccommodationDisplay(booking)}
        </div>
        <div>
          {getCourseDisplay(booking) || 'Staycation'}
        </div>
      </div>

      {/* Dates */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '8px',
        paddingTop: '12px',
        borderTop: '1px solid #E3DFD3',
        fontSize: '13px',
        color: '#5F6B61'
      }}>
        <div>
          <strong style={{ color: '#36433B' }}>Check-in:</strong><br />
          {booking.checkIn ? format(new Date(booking.checkIn), 'MMM dd, yyyy') : 'N/A'}
        </div>
        <div>
          <strong style={{ color: '#36433B' }}>Check-out:</strong><br />
          {booking.checkOut ? format(new Date(booking.checkOut), 'MMM dd, yyyy') : 'N/A'}
        </div>
      </div>

      {/* Click hint */}
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #E3DFD3',
        textAlign: 'center',
        fontSize: '13px',
        color: '#1A3E2A',
        fontWeight: '500'
      }}>
        Click to add services →
      </div>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, onSelect }) => {
  return (
    <div
      onClick={() => onSelect(service)}
      style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '2px solid transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(26, 62, 42, 0.3)';
        e.currentTarget.style.borderColor = '#1A3E2A';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#36433B' }}>
        {service.name}
      </h3>
      
      {service.description && (
        <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#5F6B61', lineHeight: '1.5' }}>
          {service.description}
        </p>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '1px solid #E3DFD3'
      }}>
        <span style={{ fontSize: '24px', fontWeight: '700', color: '#1A3E2A' }}>
          ₹{service.price}
        </span>
        <button style={{
          padding: '8px 16px',
          backgroundColor: '#1A3E2A',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer'
        }}>
          Select →
        </button>
      </div>
    </div>
  );
};

// Item Card Component
const ItemCard = ({ item, onSelect }) => {
  const stockStatusColor = {
    IN_STOCK: { bg: '#E1E9E2', text: '#163626' },
    LOW_STOCK: { bg: '#F3E6CB', text: '#B68235' },
    OUT_OF_STOCK: { bg: '#F2D9D3', text: '#8E3628' }
  };

  const statusStyle = stockStatusColor[item.stockStatus] || stockStatusColor.IN_STOCK;

  return (
    <div
      onClick={() => item.stockStatus !== 'OUT_OF_STOCK' && onSelect(item)}
      style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        cursor: item.stockStatus === 'OUT_OF_STOCK' ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        border: '2px solid transparent',
        opacity: item.stockStatus === 'OUT_OF_STOCK' ? 0.5 : 1
      }}
      onMouseEnter={(e) => {
        if (item.stockStatus !== 'OUT_OF_STOCK') {
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(26, 62, 42, 0.3)';
          e.currentTarget.style.borderColor = '#1A3E2A';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
          {item.name}
        </h3>
        {item.description && (
          <p style={{ margin: 0, fontSize: '13px', color: '#5F6B61' }}>
            {item.description}
          </p>
        )}
      </div>

      <div style={{ marginBottom: '12px', fontSize: '13px', color: '#5F6B61' }}>
        <div>SKU: {item.sku}</div>
        <div>Stock: {item.quantity} units</div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #E3DFD3'
      }}>
        <span style={{ fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
          ₹{item.unitPrice}
        </span>
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: statusStyle.bg,
          color: statusStyle.text
        }}>
          {item.stockStatus.replace('_', ' ')}
        </span>
      </div>
    </div>
  );
};

export default AddOnServicesPage;
